import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FaBars, FaTimes, FaCalendarAlt } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/images/logo-medium.png";

const Nav = styled.nav.withConfig({
  shouldForwardProp: (prop) => prop !== "scrolled",
})`
  background: ${({ scrolled }) =>
    scrolled ? "rgba(15, 17, 35, 0.95)" : "rgba(15, 17, 35, 0.6)"};
  backdrop-filter: blur(20px);
  height: 70px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 200, 255, 0.15);
  box-shadow: ${({ scrolled }) =>
    scrolled ? "0 4px 30px rgba(0, 0, 0, 0.3)" : "none"};
  transition: all 0.3s ease;

  @media (max-width: 900px) {
    padding: 0 20px;
    height: 60px;
  }
`;

const Logo = styled.img`
  height: 2.8em;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const NavLinks = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen",
})`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 900px) {
    display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: rgba(15, 17, 35, 0.98);
    backdrop-filter: blur(20px);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
    z-index: 999;
  }
`;

const NavLink = styled.a`
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.3s ease;

  &:hover {
    color: #ffffff;
    background: rgba(0, 200, 255, 0.1);
  }

  @media (max-width: 900px) {
    font-size: 18px;
    padding: 16px 32px;
    width: 80%;
    text-align: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

/*
  CTA de agendamiento. Es la única acción de la barra que lleva a /call, así que
  va destacada y no como un link más de navegación.
*/
const NavCta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
  background: linear-gradient(135deg, #00c8ff 0%, #005bea 100%);
  color: #ffffff;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 10px 20px;
  border-radius: 8px;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(0, 200, 255, 0.3);
  }

  @media (max-width: 900px) {
    margin-left: 0;
    margin-top: 20px;
    font-size: 16px;
    padding: 16px 36px;
  }
`;

const HamburgerIcon = styled.div`
  display: none;
  font-size: 24px;
  color: #ffffff;
  cursor: pointer;
  z-index: 1001;
  padding: 8px;

  @media (max-width: 900px) {
    display: flex;
    align-items: center;
  }
`;

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavigation = (sectionId) => {
    setIsOpen(false);

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const goToHome = () => {
    setIsOpen(false);
    navigate("/");
  };

  return (
    <Nav scrolled={scrolled}>
      <Logo
        src={logo}
        alt="Monaco IT Logo"
        onClick={goToHome}
        style={{ cursor: "pointer" }}
      />
      <HamburgerIcon onClick={toggleMenu}>
        {isOpen ? <FaTimes /> : <FaBars />}
      </HamburgerIcon>

      <NavLinks isOpen={isOpen}>
        <NavLink onClick={() => handleNavigation("hero")}>Inicio</NavLink>
        <NavLink onClick={() => handleNavigation("services")}>
          Servicios
        </NavLink>
        <NavLink onClick={() => handleNavigation("private-it")}>
          Área TI Privada
        </NavLink>
        <NavLink onClick={() => handleNavigation("press")}>Prensa</NavLink>
        <NavLink onClick={() => handleNavigation("about")}>Nosotros</NavLink>
        <NavLink as="a" href="/blog">Blog</NavLink>
        <NavLink onClick={() => handleNavigation("contact")}>Contacto</NavLink>
        <NavCta href="/call" onClick={() => setIsOpen(false)}>
          <FaCalendarAlt /> Agendar llamada
        </NavCta>
      </NavLinks>
    </Nav>
  );
};
